import { useEffect, useMemo, useState } from "react";
import { useGetIdentity } from "ra-core";
import { useLocation } from "react-router";
import { BookOpen, CircleHelp, Loader2, Send, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Markdown, ProposalList, sendClaudeRequest, useClaudeThread, pageContext } from "../claude/ClaudeChat";
import { HELP_TOPICS, KEYBOARD_TOPIC, MOBILE_TOPIC, helpAsText, topicFor } from "./helpContent";

// LDF: Hilfe passend zur aktuellen Seite – Fragezeichen oben rechts oder Taste „?“.

const HELP_EVENT = "ldf:open-help";
export const openHelp = () => window.dispatchEvent(new Event(HELP_EVENT));

export const HelpButton = ({ className = "" }: { className?: string }) => (
  <button
    type="button"
    onClick={openHelp}
    title="Hilfe zu dieser Seite (Taste ?)"
    aria-label="Hilfe"
    className={`inline-flex h-9 w-9 items-center justify-center rounded-full text-secondary-foreground/80 hover:bg-secondary-foreground/10 hover:text-secondary-foreground cursor-pointer ${className}`}
  >
    <CircleHelp className="h-5 w-5" />
  </button>
);

const AskClaude = ({ pathname, topicText }: { pathname: string; topicText: string }) => {
  const { identity } = useGetIdentity();
  const [question, setQuestion] = useState("");
  const [threadId, setThreadId] = useState(() => crypto.randomUUID());
  const { data: requests } = useClaudeThread(threadId);
  const pending = requests?.some((r) => r.status === "wartet" || r.status === "laeuft");

  const ask = async () => {
    if (!identity || !question.trim()) return;
    await sendClaudeRequest({
      threadId,
      salesId: identity.id,
      context: { ...pageContext(pathname), mode: "help" },
      prompt: `Hilfe-Frage zum CRM auf der Seite ${pathname}: ${question.trim()}\n\nBeantworte kurz, Schritt für Schritt, in einfachen Worten (Sie-Form ist nicht nötig, Katharina wird geduzt). Nutze vor allem dieses Handbuch; wenn etwas dort nicht steht, sag das ehrlich. Wenn es hilft, schlage open_view vor, um direkt zur richtigen Ansicht zu springen.\n\nHandbuch:\n${topicText}`,
    });
    setQuestion("");
  };

  return (
    <div className="space-y-2 rounded-lg border border-border p-3">
      <span className="flex items-center gap-1 text-sm font-medium">
        <Sparkles className="h-4 w-4" /> Frag Claude
      </span>
      {requests?.map((r) => (
        <div key={r.id} className="space-y-1">
          <p className="text-xs text-muted-foreground">Du: {r.prompt.replace(/^Hilfe-Frage zum CRM auf der Seite \S+: /, "").split("\n\n")[0]}</p>
          {r.status === "fertig" && r.answer ? (
            <>
              <Markdown text={r.answer} />
              <ProposalList request={r} />
            </>
          ) : r.status === "fehler" ? (
            <p className="text-sm text-destructive">Claude ist gerade nicht erreichbar.</p>
          ) : (
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Claude denkt nach …
            </p>
          )}
        </div>
      ))}
      <div className="flex gap-2">
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder="z. B. Wie trage ich einen Caterer ein?"
          className="h-9 min-w-0 flex-1 rounded-md border border-input bg-background px-3 text-sm"
        />
        <Button size="sm" onClick={ask} disabled={!question.trim() || pending} aria-label="Frage senden">
          <Send className="h-4 w-4" />
        </Button>
      </div>
      {!!requests?.length && (
        <button type="button" className="text-xs underline cursor-pointer text-muted-foreground" onClick={() => setThreadId(crypto.randomUUID())}>
          Neue Frage beginnen
        </button>
      )}
    </div>
  );
};

export const HelpPanel = () => {
  const [open, setOpen] = useState(false);
  const [topicId, setTopicId] = useState<string | null>(null);
  const location = useLocation();
  const pageTopic = topicFor(location.pathname);
  const ALL_TOPICS = [...HELP_TOPICS, MOBILE_TOPIC, KEYBOARD_TOPIC];
  const topic = ALL_TOPICS.find((t) => t.id === topicId) ?? pageTopic;
  const topicText = useMemo(() => helpAsText(topic), [topic]);

  useEffect(() => {
    const onOpen = () => {
      setTopicId(null);
      setOpen(true);
    };
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))) return;
      if (event.key === "?" && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        onOpen();
      }
    };
    window.addEventListener(HELP_EVENT, onOpen);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener(HELP_EVENT, onOpen);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" /> Hilfe: {topic.title}
          </SheetTitle>
          <SheetDescription>{topic.intro}</SheetDescription>
        </SheetHeader>

        <div className="space-y-5 px-4 pb-6">
          <AskClaude pathname={location.pathname} topicText={topicText} />

          {topic.sections.map((s) => (
            <section key={s.heading}>
              <h3 className="mb-1 text-sm font-semibold">{s.heading}</h3>
              <Markdown text={s.body} />
            </section>
          ))}

          {topic.howtos?.map((h) => (
            <section key={h.title} className="rounded-lg bg-muted/50 p-3">
              <h3 className="mb-1 text-sm font-semibold">So geht’s: {h.title}</h3>
              <ol className="list-decimal space-y-0.5 pl-5 text-sm">
                {h.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </section>
          ))}

          <section>
            <h3 className="mb-1 text-sm font-semibold">Andere Seiten</h3>
            <div className="flex flex-wrap gap-1">
              {ALL_TOPICS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTopicId(t.id === pageTopic.id ? null : t.id)}
                  className={`rounded-full border px-2 py-0.5 text-xs cursor-pointer ${t.id === topic.id ? "border-primary bg-primary/10" : "border-border hover:bg-muted"}`}
                >
                  {t.title}
                </button>
              ))}
            </div>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
};
